/**
 * KAN-738: блок обновления FEED-источника на странице источника.
 *
 * Переключатель ночного обновления (цены, наличие, новые товары; промпты не
 * меняются), дата последнего ночного обновления, ошибка ночного обновления и
 * кнопка полного обновления с подтверждением. Логика мутаций в useFeedSync.
 */
import { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { isAutoUpdateEnabled, supportsAutoUpdate, useFeedSync } from '@/hooks/useFeedSync';
import type { DataSource } from '@/types/dataSource';

export const FULL_REFRESH_HINT =
  'Полное обновление: каталог как в фиде, лишние товары удаляются, промпты пересобираются';
export const FULL_REFRESH_CONFIRM = 'Полное обновление каталога и промптов. Продолжить?';

function formatSyncDate(value: string | null | undefined): string {
  if (!value) return 'ещё не было';
  return new Date(value).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface FeedSyncCardProps {
  dataSource: DataSource;
}

export function FeedSyncCard({ dataSource }: FeedSyncCardProps) {
  const { setAutoUpdate, fullRefresh } = useFeedSync(dataSource.id);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const enabled = isAutoUpdateEnabled(dataSource);
  const canAutoUpdate = supportsAutoUpdate(dataSource);
  const refreshing = fullRefresh.isPending || dataSource.status === 'PROCESSING';

  return (
    <Card data-testid="feed-sync-card">
      <CardHeader>
        <CardTitle className="text-lg">Обновление каталога</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canAutoUpdate ? (
          <div className="flex items-start gap-3">
            <Switch
              id="feed-auto-update"
              data-testid="feed-auto-update-switch"
              checked={enabled}
              disabled={setAutoUpdate.isPending}
              onCheckedChange={(checked) => setAutoUpdate.mutate(checked ? 'daily' : 'off')}
            />
            <div className="space-y-1">
              <Label htmlFor="feed-auto-update" className="text-sm font-medium text-gray-900">
                Обновлять каждую ночь (цены, наличие, новые товары)
              </Label>
              <p className="text-xs text-gray-500">
                Промпты при ночном обновлении не меняются. Кнопка Обновить полностью пересобирает
                каталог по фиду и обновляет промпты.
              </p>
            </div>
          </div>
        ) : (
          <p data-testid="feed-auto-update-unavailable" className="text-sm text-gray-600">
            Ночное обновление доступно только для фида по ссылке.
          </p>
        )}

        {canAutoUpdate && (
          <div>
            <p className="text-xs text-gray-500">Последнее ночное обновление</p>
            <p data-testid="feed-last-synced" className="text-sm text-gray-700">
              {formatSyncDate(dataSource.last_synced_at)}
            </p>
          </div>
        )}

        {dataSource.sync_error && (
          <div
            data-testid="feed-sync-error"
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <span>Ошибка ночного обновления: {dataSource.sync_error}</span>
          </div>
        )}

        <div className="space-y-1 border-t pt-4">
          <Button
            data-testid="feed-full-refresh-button"
            variant="outline"
            title={FULL_REFRESH_HINT}
            disabled={refreshing}
            onClick={() => setConfirmOpen(true)}>
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            {refreshing ? 'Обновление…' : 'Обновить'}
          </Button>
          <p className="text-xs text-gray-500">{FULL_REFRESH_HINT}</p>
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="feed-full-refresh-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Полное обновление</AlertDialogTitle>
            <AlertDialogDescription>{FULL_REFRESH_CONFIRM}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="feed-full-refresh-cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction
              data-testid="feed-full-refresh-confirm-button"
              onClick={() => fullRefresh.mutate()}>
              Обновить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default FeedSyncCard;
